import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TmTaskStatus } from "../../api/tm";

const ALLOWED: Record<TmTaskStatus, TmTaskStatus[]> = {
  TODO:        ["IN_PROGRESS", "BLOCKED", "CANCELLED"],
  IN_PROGRESS: ["IN_REVIEW", "BLOCKED", "CANCELLED", "TODO"],
  IN_REVIEW:   ["DONE", "IN_PROGRESS", "CANCELLED"],
  BLOCKED:     ["TODO", "IN_PROGRESS", "CANCELLED"],
  DONE:        ["IN_PROGRESS"],
  CANCELLED:   [],
};

/** Per-status pill colors — matches the look of TmStatusBadge so the dropdown
 * reads as "this is the current state, click to move it". */
const PALETTE: Record<TmTaskStatus, { bg: string; fg: string; label: string }> = {
  TODO:        { bg: "var(--surface-muted)", fg: "var(--ink-3)", label: "To do" },
  IN_PROGRESS: { bg: "var(--info-bg)", fg: "var(--info)", label: "In progress" },
  IN_REVIEW:   { bg: "var(--warn-bg)", fg: "var(--warn)", label: "In review" },
  BLOCKED:     { bg: "var(--err-bg)", fg: "var(--err)", label: "Blocked" },
  DONE:        { bg: "var(--ok-bg)", fg: "var(--ok)", label: "Done" },
  CANCELLED:   { bg: "var(--surface-muted)", fg: "var(--ink-3)", label: "Cancelled" },
};

type Props = {
  current: TmTaskStatus;
  onTransition: (next: TmTaskStatus, payload: { comment?: string; blockedReason?: string }) => Promise<void>;
};

/**
 * Jira-style status dropdown. The current state lives in a pill that opens a
 * popover with allowed transitions. Two transitions need a reason — BLOCKED
 * (why), and "send back from review" — both open a small modal first.
 */
export function TmStatusActionMenu({ current, onTransition }: Props) {
  const { canWrite } = useSectionAccess("tm_tasks");
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<TmTaskStatus | null>(null);
  const [text, setText] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function close(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  async function fire(next: TmTaskStatus) {
    if (!canWrite) return;
    setOpen(false);
    if (next === "BLOCKED") { setPending(next); return; }
    await onTransition(next, {});
  }

  async function confirm() {
    if (!canWrite) return;
    if (!pending) return;
    if (pending === "BLOCKED") await onTransition(pending, { blockedReason: text });
    else await onTransition(pending, { comment: text });
    setPending(null);
    setText("");
  }

  // An unknown status has no known transitions, which is an empty menu — not a reason to take the
  // screen down. Same rule as the palette below: the browser may be older than the server.
  const next = canWrite ? ALLOWED[current] ?? [] : [];
  // A state the browser does not know is a state the server added since this bundle was built,
  // which is exactly what a reader with an open tab sees during a release. An unknown state is
  // shown plainly rather than taking the screen down with it.
  const cur = PALETTE[current] ?? { bg: "var(--surface-2)", fg: "var(--ink-2)" };

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onMouseDown={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        disabled={next.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "6px 12px",
          background: cur.bg, color: cur.fg,
          border: `1px solid ${cur.fg}33`,
          borderRadius: 6,
          fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
          cursor: next.length === 0 ? "default" : "pointer",
          opacity: next.length === 0 ? 0.7 : 1,
        }}
      >
        <span>{t(`tm.status.${current}`, { defaultValue: cur.label })}</span>
        {next.length > 0 && <span aria-hidden style={{ fontSize: 10 }}>▾</span>}
      </button>

      {open && next.length > 0 && (
        <div
          role="listbox"
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0,
            minWidth: 180, background: "var(--surface)",
            border: "1px solid var(--border)", borderRadius: 8,
            boxShadow: "var(--shadow-2)",
            padding: 6, zIndex: 30,
          }}
        >
          <div style={{
            padding: "4px 8px", fontSize: 10, fontWeight: 700,
            color: "var(--ink-4)", textTransform: "uppercase", letterSpacing: 0.4,
          }}>
            {t("tm.status.moveTo", "Move to")}
          </div>
          {next.map((n) => {
            const p = PALETTE[n];
            return (
              <button
                key={n}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); void fire(n); }}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  width: "100%", textAlign: "left",
                  padding: "6px 8px", borderRadius: 6,
                  background: "transparent", border: "none", cursor: "pointer",
                  fontSize: 13, color: "var(--ink)",
                }}
                onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.background = "var(--surface-2)"; }}
                onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.background = "transparent"; }}
              >
                <span style={{
                  display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                  background: p.fg, flex: "none",
                }} />
                <span style={{ flex: 1 }}>{t(`tm.status.${n}`, { defaultValue: p.label })}</span>
              </button>
            );
          })}
        </div>
      )}

      {pending && (
        <div style={{
          position: "fixed", inset: 0, background: "var(--overlay)",
          display: "grid", placeItems: "center", zIndex: 50, padding: 16,
        }}>
          <div style={{
            background: "var(--surface)", padding: 18, borderRadius: 10,
            width: "min(420px, 100%)", display: "flex", flexDirection: "column", gap: 10,
            boxShadow: "var(--shadow-2)",
          }} onClick={(e) => e.stopPropagation()}>
            <strong style={{ color: "var(--ink)" }}>
              {pending === "BLOCKED"
                ? t("tm.status.blockedReason", "Reason for blocking")
                : t("tm.status.returnComment", "Why are you returning this?")}
            </strong>
            <textarea
              className="input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              autoFocus
            />
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button type="button" className="btn" onClick={() => { setPending(null); setText(""); }}>
                {t("action.cancel", "Cancel")}
              </button>
              <button type="button" className="btn primary" onClick={confirm} disabled={!canWrite || !text.trim()}>
                {t("action.confirm", "Confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
